import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { ArrowLeft, MessageSquare, Bug, Lightbulb, Star, Mail, User, Calendar, Filter } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const FeedbackDashboard = () => {
  const navigate = useNavigate();
  const [feedbacks, setFeedbacks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterType, setFilterType] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');

  useEffect(() => {
    fetchFeedback();
  }, []);

  const fetchFeedback = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/feedback`, {
        withCredentials: true
      });
      setFeedbacks(response.data);
    } catch (error) {
      console.error('Error fetching feedback:', error);
      if (error.response?.status === 401) {
        alert('You need to be logged in to view feedback');
        navigate('/auth');
      }
    } finally {
      setLoading(false);
    }
  };

  const getTypeIcon = (type) => {
    switch(type) {
      case 'bug': return Bug;
      case 'feature': return Lightbulb;
      case 'improvement': return Star;
      default: return MessageSquare;
    }
  };

  const getTypeColor = (type) => {
    switch(type) {
      case 'bug': return 'bg-red-100 text-red-700';
      case 'feature': return 'bg-blue-100 text-blue-700';
      case 'improvement': return 'bg-purple-100 text-purple-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const getStatusColor = (status) => {
    switch(status) {
      case 'new': return 'bg-green-100 text-green-700';
      case 'in_progress': return 'bg-yellow-100 text-yellow-700';
      case 'resolved': return 'bg-blue-100 text-blue-700';
      case 'closed': return 'bg-gray-100 text-gray-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const filteredFeedbacks = feedbacks.filter(fb => {
    if (filterType !== 'all' && fb.feedback_type !== filterType) return false;
    if (filterStatus !== 'all' && fb.status !== filterStatus) return false;
    return true;
  });

  const stats = {
    total: feedbacks.length,
    new: feedbacks.filter(f => f.status === 'new').length,
    bugs: feedbacks.filter(f => f.feedback_type === 'bug').length,
    features: feedbacks.filter(f => f.feedback_type === 'feature').length,
    avgRating: feedbacks.filter(f => f.rating).length > 0 
      ? (feedbacks.reduce((sum, f) => sum + (f.rating || 0), 0) / feedbacks.filter(f => f.rating).length).toFixed(1)
      : 'N/A'
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-orange-50">
      {/* Header */}
      <header className="border-b bg-white/80 backdrop-blur-sm sticky top-0 z-50">
        <div className="container mx-auto px-6 py-4 flex items-center justify-between">
          <Button
            variant="ghost"
            className="gap-2"
            onClick={() => navigate('/')}
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Button>
          <h1 className="text-2xl font-bold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">
            Feedback Dashboard
          </h1>
          <Button onClick={fetchFeedback}>Refresh</Button>
        </div>
      </header>

      <div className="container mx-auto px-6 py-8 max-w-7xl">
        {/* Stats */}
        <div className="grid md:grid-cols-5 gap-4 mb-8">
          <Card>
            <CardContent className="p-4 text-center">
              <div className="text-2xl font-bold text-purple-600">{stats.total}</div>
              <div className="text-sm text-slate-600">Total Feedback</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <div className="text-2xl font-bold text-green-600">{stats.new}</div>
              <div className="text-sm text-slate-600">New</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <div className="text-2xl font-bold text-red-600">{stats.bugs}</div>
              <div className="text-sm text-slate-600">Bugs</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <div className="text-2xl font-bold text-blue-600">{stats.features}</div>
              <div className="text-sm text-slate-600">Features</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <div className="text-2xl font-bold text-yellow-600">{stats.avgRating}</div>
              <div className="text-sm text-slate-600">Avg Rating</div>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <Card className="mb-6">
          <CardContent className="p-4">
            <div className="flex flex-wrap gap-4 items-center">
              <div className="flex items-center gap-2">
                <Filter className="w-4 h-4 text-slate-600" />
                <span className="text-sm font-semibold text-slate-700">Filters:</span>
              </div>
              <div className="flex gap-2">
                <select
                  value={filterType}
                  onChange={(e) => setFilterType(e.target.value)}
                  className="px-3 py-1 border border-slate-300 rounded-lg text-sm"
                >
                  <option value="all">All Types</option>
                  <option value="bug">Bugs</option>
                  <option value="feature">Features</option>
                  <option value="improvement">Improvements</option>
                  <option value="other">Other</option>
                </select>
                <select
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value)}
                  className="px-3 py-1 border border-slate-300 rounded-lg text-sm"
                >
                  <option value="all">All Status</option>
                  <option value="new">New</option>
                  <option value="in_progress">In Progress</option>
                  <option value="resolved">Resolved</option>
                  <option value="closed">Closed</option>
                </select>
              </div>
              <div className="text-sm text-slate-600">
                Showing {filteredFeedbacks.length} of {feedbacks.length}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Feedback List */}
        {loading ? (
          <div className="text-center py-12">
            <div className="text-slate-600">Loading feedback...</div>
          </div>
        ) : filteredFeedbacks.length === 0 ? (
          <Card>
            <CardContent className="p-12 text-center">
              <MessageSquare className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-600">No feedback found</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {filteredFeedbacks.map((feedback) => {
              const TypeIcon = getTypeIcon(feedback.feedback_type);
              return (
                <Card key={feedback.id} className="hover:shadow-lg transition-shadow">
                  <CardHeader>
                    <div className="flex items-start justify-between">
                      <div className="flex items-start gap-3 flex-1">
                        <div className={`w-10 h-10 rounded-lg ${getTypeColor(feedback.feedback_type)} flex items-center justify-center flex-shrink-0`}>
                          <TypeIcon className="w-5 h-5" />
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <Badge className={getTypeColor(feedback.feedback_type)}>
                              {feedback.feedback_type}
                            </Badge>
                            <Badge className={getStatusColor(feedback.status)}>
                              {feedback.status}
                            </Badge>
                            {feedback.category && (
                              <Badge variant="outline">{feedback.category}</Badge>
                            )}
                          </div>
                          <p className="text-slate-700 leading-relaxed">{feedback.message}</p>
                        </div>
                      </div>
                      {feedback.rating && (
                        <div className="flex items-center gap-1 ml-4">
                          {[...Array(5)].map((_, i) => (
                            <Star
                              key={i}
                              className={`w-4 h-4 ${
                                i < feedback.rating
                                  ? 'fill-yellow-400 text-yellow-400'
                                  : 'text-slate-300'
                              }`}
                            />
                          ))}
                        </div>
                      )}
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="flex flex-wrap gap-4 text-sm text-slate-600">
                      {feedback.user_name && (
                        <div className="flex items-center gap-1">
                          <User className="w-4 h-4" />
                          {feedback.user_name}
                        </div>
                      )}
                      {feedback.user_email && (
                        <div className="flex items-center gap-1">
                          <Mail className="w-4 h-4" />
                          {feedback.user_email}
                        </div>
                      )}
                      <div className="flex items-center gap-1">
                        <Calendar className="w-4 h-4" />
                        {new Date(feedback.created_at).toLocaleDateString('en-US', {
                          month: 'short',
                          day: 'numeric',
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </div>
                      {feedback.user_id && (
                        <Badge variant="outline" className="text-xs">
                          Authenticated User
                        </Badge>
                      )}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default FeedbackDashboard;
